// Input validation and sanitization utilities

/**
 * Validates and sanitizes flight number input
 * Accepts formats like: UA123, UA 123, UA1, UA1234
 * @param {string} flightNumber - The flight number to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateFlightNumber = (flightNumber) => {
  if (!flightNumber || flightNumber.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  // Remove extra whitespace and convert to uppercase
  const sanitized = flightNumber.trim().toUpperCase();

  // Flight number pattern: 2-3 letter airline code + 1-4 digit number
  // Examples: UA123, AA1, DL9876
  const pattern = /^[A-Z]{2,3}\s?\d{1,4}$/;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 10), // Limit length
      error: 'Invalid format (e.g., UA123)',
    };
  }

  // Remove space between airline code and number for consistency
  const normalized = sanitized.replace(/\s+/g, ' ').trim();

  return {
    isValid: true,
    sanitized: normalized,
    error: '',
  };
};

/**
 * Validates airport code (IATA 3-letter or ICAO 4-letter)
 * @param {string} code - The airport code to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateAirportCode = (code) => {
  if (!code || code.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  // Convert to uppercase and remove whitespace
  const sanitized = code.trim().toUpperCase().replace(/\s+/g, '');

  // Must be 3 or 4 letters only
  const pattern = /^[A-Z]{3,4}$/;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 4),
      error: 'Must be 3-4 letters',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: '',
  };
};

/**
 * Validates duration format (Xh Ym)
 * @param {string} duration - The duration to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateDuration = (duration) => {
  if (!duration || duration.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  const sanitized = duration.trim();

  // Pattern: Xh Ym or X:YY or just Xh
  // Examples: 5h 25m, 12h 0m, 2h, 5:25
  const pattern = /^(\d{1,2}h\s?\d{0,2}m?|\d{1,2}:\d{2}|\d{1,2}h)$/i;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 10),
      error: 'Format: Xh Ym (e.g., 5h 25m)',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: '',
  };
};

/**
 * Validates aircraft tail number/registration
 * @param {string} tailNumber - The tail number to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateTailNumber = (tailNumber) => {
  if (!tailNumber || tailNumber.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  const sanitized = tailNumber.trim().toUpperCase();

  // US registration: N followed by 1-5 alphanumeric characters
  // International: 2-6 alphanumeric characters (various formats)
  const pattern = /^[A-Z0-9]{2,7}$/;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 7).replace(/[^A-Z0-9]/g, ''),
      error: 'Invalid tail number',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: '',
  };
};

/**
 * Validates gate number
 * @param {string} gate - The gate to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateGate = (gate) => {
  if (!gate || gate.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  const sanitized = gate.trim().toUpperCase();

  // Gate format: Letter(s) + Number(s), or just number
  // Examples: A12, B3, T1, 42, D24A
  const pattern = /^[A-Z]?\d{1,3}[A-Z]?$/;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 5).replace(/[^A-Z0-9]/g, ''),
      error: 'Invalid gate format',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: '',
  };
};

/**
 * Validates seat number
 * @param {string} seat - The seat to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateSeat = (seat) => {
  if (!seat || seat.trim() === '') {
    return { isValid: true, sanitized: '', error: '' };
  }

  const sanitized = seat.trim().toUpperCase();

  // Seat format: 1-3 digits + letter
  // Examples: 12A, 1F, 42K
  const pattern = /^\d{1,3}[A-K]$/;

  if (!pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized: sanitized.slice(0, 4).replace(/[^A-Z0-9]/g, ''),
      error: 'Format: number + letter (e.g., 12A)',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: '',
  };
};

/**
 * Sanitizes generic text input to prevent XSS
 * @param {string} text - The text to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized text
 */
export const sanitizeText = (text, maxLength = 100) => {
  if (!text) return '';

  return text
    .trim()
    .slice(0, maxLength)
    .replace(/[<>'"]/g, '') // Remove potential XSS characters
    .replace(/\s+/g, ' '); // Normalize whitespace
};

/**
 * Validates complete flight data for minting
 * @param {object} flightData - The complete flight data object
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateFlightData = (flightData) => {
  const errors = {};

  // Required fields
  if (!flightData.flightNumber) {
    errors.flightNumber = 'Flight number is required';
  } else {
    const result = validateFlightNumber(flightData.flightNumber);
    if (!result.isValid) {
      errors.flightNumber = result.error;
    }
  }

  if (!flightData.date) {
    errors.date = 'Date is required';
  }

  if (!flightData.departure) {
    errors.departure = 'Departure airport is required';
  } else {
    const result = validateAirportCode(flightData.departure);
    if (!result.isValid) {
      errors.departure = result.error;
    }
  }

  if (!flightData.arrival) {
    errors.arrival = 'Arrival airport is required';
  } else {
    const result = validateAirportCode(flightData.arrival);
    if (!result.isValid) {
      errors.arrival = result.error;
    }
  }

  // Optional field validation
  if (flightData.duration) {
    const result = validateDuration(flightData.duration);
    if (!result.isValid) {
      errors.duration = result.error;
    }
  }

  if (flightData.tailNumber) {
    const result = validateTailNumber(flightData.tailNumber);
    if (!result.isValid) {
      errors.tailNumber = result.error;
    }
  }

  if (flightData.departureGate) {
    const result = validateGate(flightData.departureGate);
    if (!result.isValid) {
      errors.departureGate = result.error;
    }
  }

  if (flightData.arrivalGate) {
    const result = validateGate(flightData.arrivalGate);
    if (!result.isValid) {
      errors.arrivalGate = result.error;
    }
  }

  if (flightData.seat) {
    const result = validateSeat(flightData.seat);
    if (!result.isValid) {
      errors.seat = result.error;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
